import { Backspace } from '@mui/icons-material';
import { Box, Grid, IconButton } from '@mui/material';
import {
  ChangeEvent,
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ClientCafe,
  Dish,
  Ingredient,
  Kitchen,
  LegalEntity,
  MenuCategory,
  Order,
  PosShift,
  Product,
  Promotion,
  Reservation,
  Review,
  Section,
  SelfOrderPoint,
  SemiProduct,
  Shift,
  Store,
  Wallet,
  Worker,
} from '../apiTypes';
import Confirmation from '../components/Confirmation';
import Loader from '../components/Loader';
import Logo from '../components/Logo';
import PrimaryButton from '../components/PrimaryButton';
import StyledTextField from '../components/styled/StyledTextField';
import { t } from '../constants/translations';
import { useCollection } from '../utils/api';
import { getDayTimeRange } from '../utils/dates';
import { getInitMap, Map } from '../utils/map';
import turnOffMultyUser from '../utils/turnOffMultyUser';
import useIdleTimeout from '../utils/useIdleTimeout';
import { useLocale } from './locale.context';
import { useUser } from './user.context';

type Params = {
  cafeSlug: string;
};

type CafeContextValue = {
  isLoading: boolean;
  shifts: DbDocument<Shift>[];
  worker?: DbDocument<Worker>;
  dishes: DbDocument<Dish>[];
  dishesMap: Map<DbDocument<Dish>>;
  categories: DbDocument<MenuCategory>[];
  categoriesMap: Map<DbDocument<MenuCategory>>;
  sections: DbDocument<Section>[];
  sectionsMap: Map<DbDocument<Section>>;
  products: DbDocument<Product>[];
  productsMap: Map<DbDocument<Product>>;
  semiProducts: DbDocument<SemiProduct>[];
  semiProductsMap: Map<DbDocument<SemiProduct>>;
  ingredients: Ingredient[];
  ingredientsMap: Map<Ingredient>;
  stores: DbDocument<Store>[];
  kitchens: DbDocument<Kitchen>[];
  kitchensMap: Map<DbDocument<Kitchen>>;
  workers: DbDocument<Worker>[];
  workersMap: Map<DbDocument<Worker>>;
  storesMap: Map<DbDocument<Store>>;
  wallets: DbDocument<Wallet>[];
  walletsMap: Map<DbDocument<Wallet>>;
  clientGroups: DbDocument<ClientCafe>[];
  orders: DbDocument<Order>[];
  regularOrders: DbDocument<Order>[];
  deliveryOrders: DbDocument<Order>[];
  promotions: DbDocument<Promotion>[];
  legalEntities: DbDocument<LegalEntity>[];
  legalEntitiesMap: Map<DbDocument<LegalEntity>>;
  selfOrderPoints: DbDocument<SelfOrderPoint>[];
  selfOrderPointsMap: Map<DbDocument<SelfOrderPoint>>;
  unConfirmedReservations: DbDocument<Reservation>[];
  reservations: DbDocument<Reservation>[];
  isMultyUser: boolean;
  clearPin: () => void;
  openMessages: boolean;
  setOpenMessages: (isOpen: boolean) => void;
  newReviews: DbDocument<Review>[];
  currentPosShift?: DbDocument<PosShift>;
};

const emptyArray: never[] = [];

const CafeContext = createContext<CafeContextValue | null>(null);

export const useCafe = (): CafeContextValue => {
  const context = useContext(CafeContext);
  if (!context) {
    throw new Error('useCafe must be used within a CafeProvider');
  }

  return context;
};

export const CafeProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const { lang } = useLocale();
  const { cafeSlug } = useParams<Params>();
  const {
    user: { email, phone, id },
  } = useUser();
  const [timeRange, setTimeRange] = useState<[number, number]>(getDayTimeRange(new Date().getTime()));
  const [pin, setPin] = useState('');
  const [openMessages, setOpenMessages] = useState(false);
  const [disableMultyUser, setDisableMultyUser] = useState(false);
  const multyUser = window.localStorage.getItem('multyuser');

  const handleChangePin = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (val.length < 5) {
        setPin(val);
      }
      if (pin.length === 3) {
        navigate(`/${cafeSlug}/pos`);
      }
    },
    [cafeSlug, navigate, pin.length]
  );

  const handleClearPin = useCallback(() => {
    setPin('');
  }, []);

  const handleNumClick = useCallback(
    (num: string) => {
      if (pin.length < 4) {
        setPin((prev) => `${prev}${num}`);
      }
      if (pin.length === 3) {
        navigate(`/${cafeSlug}/pos`);
      }
    },
    [cafeSlug, navigate, pin.length]
  );

  useIdleTimeout(!!multyUser && pin.length === 4, handleClearPin, (Number(multyUser) || 20) * 1000);

  const { data: shifts, isValidating: isShiftsLoading } = useCollection<Shift>(id ? `cafes/${cafeSlug}/shifts` : null, {
    where: ['start', '>=', timeRange[0] || 0],
    where2: ['start', '<=', timeRange[1] || 0],
  });

  const { data: dishes, isValidating: isDishesLoading } = useCollection<Dish>(id ? `cafes/${cafeSlug}/dishes` : null, {
    orderBy: ['order', 'asc'],
  });

  const { data: categories, isValidating: isCategoriesLoading } = useCollection<MenuCategory>(
    `cafes/${cafeSlug}/categories`,
    { orderBy: ['order', 'asc'] }
  );

  const { data: sections, isValidating: isSectionsLoading } = useCollection<Section>(
    id ? `cafes/${cafeSlug}/sections` : null,
    {
      orderBy: ['order', 'asc'],
    }
  );

  const { data: products, isValidating: isProductsLoading } = useCollection<Product>(
    id ? `cafes/${cafeSlug}/products` : null
  );

  const { data: semiProducts, isValidating: isSemiProductsLoading } = useCollection<SemiProduct>(
    id ? `cafes/${cafeSlug}/semiProducts` : null
  );

  const { data: stores, isValidating: isStoresLoading } = useCollection<Store>(id ? `cafes/${cafeSlug}/stores` : null, {
    where: ['active', '==', true],
  });

  const { data: kitchens, isValidating: isKitchensLoading } = useCollection<Kitchen>(
    id ? `cafes/${cafeSlug}/kitchens` : null
  );

  const { data: workers, isValidating: isWorkersLoading } = useCollection<Worker>(
    id ? `cafes/${cafeSlug}/workers` : null
  );

  const { data: wallets, isValidating: isWalletsLoading } = useCollection<Wallet>(
    id ? `cafes/${cafeSlug}/wallets` : null
  );

  const { data: orders, isValidating: isOrdersLoading } = useCollection<Order>(id ? `cafes/${cafeSlug}/orders` : null, {
    where: ['closed', '==', false],
  });

  const { data: unConfirmedReservations, isValidating: isUnConfirmedReservationsLoading } = useCollection<Reservation>(
    id ? `cafes/${cafeSlug}/reservations` : null,
    {
      where: ['confirmed', '==', false],
    }
  );

  const { data: newReviews, isValidating: isNewReviewsLoading } = useCollection<Review>(
    id ? `cafes/${cafeSlug}/reviews` : null,
    {
      where: ['isNew', '==', true],
    }
  );

  const { data: reservations, isValidating: isReservationsLoading } = useCollection<Reservation>(
    id ? `cafes/${cafeSlug}/reservations` : null,
    {
      where: ['date', '>=', timeRange[0] || 0],
      where2: ['date', '<=', timeRange[1] || 0],
    }
  );

  const { data: currentPosShifts, isValidating: isCurrentPosShiftsLoading } = useCollection<PosShift>(
    id ? `cafes/${cafeSlug}/posShifts` : null,
    {
      where: ['status', '==', 'created'],
    }
  );

  const { data: promotions, isValidating: isPromotionsLoading } = useCollection<Promotion>(
    id ? `cafes/${cafeSlug}/promotions` : null
  );

  const { data: legalEntities, isValidating: isLegalEntitiesLoading } = useCollection<LegalEntity>(
    id ? `cafes/${cafeSlug}/legalEntities` : null
  );

  const { data: selfOrderPoints, isValidating: isSelfOrderPointsLoading } = useCollection<SelfOrderPoint>(
    id ? `cafes/${cafeSlug}/selfOrderPoints` : null
  );

  const { data: clientGroups, isValidating: isClientGroupsLoading } = useCollection<ClientCafe>(
    id ? `cafes/${cafeSlug}/clients` : null,
    {
      where: ['type', '==', 'group'],
    }
  );

  const dishesMap = useMemo(
    () => (dishes || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<Dish>>()),
    [dishes]
  );

  const productsMap = useMemo(
    () => (products || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<Product>>()),
    [products]
  );

  const semiProductsMap = useMemo(
    () => (semiProducts || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<SemiProduct>>()),
    [semiProducts]
  );

  const categoriesMap = useMemo(
    () => (categories || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<MenuCategory>>()),
    [categories]
  );

  const sectionsMap = useMemo(
    () => (sections || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<Section>>()),
    [sections]
  );

  const storesMap = useMemo(
    () => (stores || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<Store>>()),
    [stores]
  );

  const kitchensMap = useMemo(
    () => (kitchens || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<Kitchen>>()),
    [kitchens]
  );

  const workersMap = useMemo(
    () => (workers || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<Worker>>()),
    [workers]
  );

  const walletsMap = useMemo(
    () => (wallets || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<Wallet>>()),
    [wallets]
  );

  const legalEntitiesMap = useMemo(
    () => (legalEntities || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<LegalEntity>>()),
    [legalEntities]
  );

  const selfOrderPointsMap = useMemo(
    () => (selfOrderPoints || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<DbDocument<SelfOrderPoint>>()),
    [selfOrderPoints]
  );

  const ingredients: Ingredient[] = useMemo(
    () => [
      ...(products || []).map(({ id, unit, imageUrl, name, category, qpp }) => ({
        type: 'product' as const,
        id,
        unit,
        qpp,
        imageUrl,
        name,
        category,
      })),
      ...(semiProducts || []).map(({ id, unit, imageUrl, name }) => ({
        type: 'semiProduct' as const,
        id,
        unit,
        imageUrl,
        name,
        category: t.semifinished_products[lang],
      })),
    ],
    [lang, products, semiProducts]
  );

  const ingredientsMap = useMemo(
    () => (ingredients || []).reduce((acc, s) => ({ ...acc, [s.id]: s }), getInitMap<Ingredient>()),
    [ingredients]
  );

  const worker = useMemo(() => {
    if (multyUser && pin.length === 4) {
      return (workers || []).find((w) => w.pin === pin);
    }
    const foundWorker = (workers || []).find((w) => (email && w.email === email) || (phone && w.phone === phone));
    if (!multyUser && foundWorker) {
      return foundWorker;
    }
    if (email === 'max.popov.mail@gmail.com') {
      return (workers || []).find((w) => w.role === 'owner');
    }
    return undefined;
  }, [email, multyUser, phone, pin, workers]);

  const regularOrders = useMemo(() => (orders || []).filter((order) => order.type !== 'delivery'), [orders]);
  const deliveryOrders = useMemo(
    () => (orders || []).filter((order) => order.type === 'delivery').sort((a, b) => (a > b ? 1 : -1)),
    [orders]
  );

  useEffect(() => {
    const id = setInterval(() => {
      setTimeRange(getDayTimeRange(new Date().getTime()));
    }, 3600000);

    return () => {
      clearInterval(id);
    };
  }, []);

  const value: CafeContextValue = useMemo(
    () => ({
      isLoading:
        isShiftsLoading ||
        isWorkersLoading ||
        isProductsLoading ||
        isDishesLoading ||
        isSemiProductsLoading ||
        isCategoriesLoading ||
        isSectionsLoading ||
        isStoresLoading ||
        isWalletsLoading ||
        isOrdersLoading ||
        isKitchensLoading ||
        isUnConfirmedReservationsLoading ||
        isNewReviewsLoading ||
        isReservationsLoading ||
        isCurrentPosShiftsLoading ||
        isPromotionsLoading ||
        isLegalEntitiesLoading ||
        isClientGroupsLoading ||
        isSelfOrderPointsLoading,
      shifts: shifts || emptyArray,
      worker,
      dishes: dishes || emptyArray,
      dishesMap,
      categories: categories || emptyArray,
      categoriesMap,
      sections: sections || emptyArray,
      sectionsMap,
      products: products || emptyArray,
      productsMap,
      semiProducts: semiProducts || emptyArray,
      semiProductsMap,
      ingredients,
      ingredientsMap,
      stores: stores || emptyArray,
      storesMap,
      kitchens: kitchens || emptyArray,
      kitchensMap,
      workers: workers || emptyArray,
      workersMap,
      wallets: wallets || emptyArray,
      walletsMap,
      clientGroups: clientGroups || emptyArray,
      orders: orders || emptyArray,
      regularOrders,
      deliveryOrders,
      unConfirmedReservations: unConfirmedReservations || emptyArray,
      reservations: reservations || emptyArray,
      newReviews: newReviews || emptyArray,
      promotions: promotions || emptyArray,
      legalEntities: legalEntities || emptyArray,
      legalEntitiesMap,
      selfOrderPoints: selfOrderPoints || emptyArray,
      selfOrderPointsMap,
      isMultyUser: !!multyUser,
      clearPin: handleClearPin,
      openMessages,
      setOpenMessages,
      currentPosShift: currentPosShifts?.[0],
    }),
    [
      isShiftsLoading,
      isWorkersLoading,
      isProductsLoading,
      isDishesLoading,
      isSemiProductsLoading,
      isCategoriesLoading,
      isSectionsLoading,
      isStoresLoading,
      isWalletsLoading,
      isOrdersLoading,
      isKitchensLoading,
      isUnConfirmedReservationsLoading,
      isNewReviewsLoading,
      isReservationsLoading,
      isCurrentPosShiftsLoading,
      isPromotionsLoading,
      isLegalEntitiesLoading,
      isClientGroupsLoading,
      isSelfOrderPointsLoading,
      shifts,
      worker,
      dishes,
      dishesMap,
      categories,
      categoriesMap,
      sections,
      sectionsMap,
      products,
      productsMap,
      semiProducts,
      semiProductsMap,
      ingredients,
      ingredientsMap,
      stores,
      storesMap,
      kitchens,
      kitchensMap,
      workers,
      workersMap,
      wallets,
      walletsMap,
      clientGroups,
      orders,
      regularOrders,
      deliveryOrders,
      unConfirmedReservations,
      reservations,
      newReviews,
      promotions,
      legalEntities,
      legalEntitiesMap,
      selfOrderPoints,
      selfOrderPointsMap,
      multyUser,
      handleClearPin,
      openMessages,
      currentPosShifts,
    ]
  );

  if (value.isLoading) {
    return <Loader open fullScreen />;
  }

  if (multyUser && !worker) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Logo
          sx={{
            width: 200,
            mb: 6,
          }}
        />

        <StyledTextField
          id="pin"
          type="password"
          label={t.pin[lang]}
          value={pin}
          onChange={handleChangePin}
          sx={{ mb: 5, width: 220 }}
          error={pin.length === 4 && !worker}
          autoComplete="off"
        />

        <Grid container maxWidth={300}>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
            <Grid item xs={4} key={num} textAlign="center" sx={{ mb: 3 }}>
              <IconButton sx={{ width: 44 }} onClick={() => handleNumClick(num)}>
                {num}
              </IconButton>
            </Grid>
          ))}

          <Grid item xs={4} textAlign="center" sx={{ mb: 3 }} />

          <Grid item xs={4} textAlign="center" sx={{ mb: 3 }}>
            <IconButton sx={{ width: 44 }} onClick={() => handleNumClick('0')}>
              0
            </IconButton>
          </Grid>

          <Grid item xs={4} textAlign="center" sx={{ mb: 3 }}>
            <IconButton sx={{ width: 44 }} onClick={handleClearPin}>
              <Backspace />
            </IconButton>
          </Grid>
        </Grid>

        {/* {deliveryOrders.length > 0 && (
          <Box
            sx={{
              position: 'fixed',
              left: 20,
              top: 20,
              right: isMobile ? 20 : undefined,
              maxHeight: 100,
              overflow: 'hidden',
            }}
          >
            <DeliveryOrdersList deliveryOrders={deliveryOrders} />
          </Box>
        )} */}

        <Box sx={{ position: 'fixed', bottom: 0 }}>
          <PrimaryButton variant="text" onClick={() => setDisableMultyUser(true)}>
            {t.disable_multi_user[lang]}
          </PrimaryButton>
        </Box>

        {disableMultyUser && (
          <Confirmation
            open={disableMultyUser}
            title={t.confirm_disable_multi_user_mode[lang]}
            onClose={() => setDisableMultyUser(false)}
            onSubmit={turnOffMultyUser}
          />
        )}
      </Box>
    );
  }

  return <CafeContext.Provider value={value}>{children}</CafeContext.Provider>;
};
